import React, { useContext } from 'react'
import { CaseDetailContext } from './CaseDetailContext'

/**
 * LegalCase — "Case Information" card matching Scale's layout.
 *
 * Standard case (5-column top row):
 *   Referring Judge / Case Type / Plaintiff's Last Demand /
 *   Defendant's Last Offer / [ODR-only: Has Litigation Filed + Date Case Filed]
 *
 * Bottom row (3 cols): Causes of Action / Remaining Discovery / Remaining Law and Motion.
 *
 * CAR case top row (4 cols): Dispute Type / Dispute Value / Filed in Court.
 */

function Field({ label, value, labelClass = 'text-xs text-black' }) {
    return (
        <div className="min-w-0">
            <h3 className={`${labelClass} leading-4`}>{label}</h3>
            <p className="mt-1 text-xs font-semibold text-[#111827] max-w-xs whitespace-nowrap overflow-hidden text-ellipsis">
                {value || 'N/A'}
            </p>
        </div>
    )
}

// These fields are authored in Scale's rich-text editor and stored as HTML
// (with <p>/<strong>/<em>/<ul>/<ol>/<a>). Render via dangerouslySetInnerHTML
// when HTML is detected; otherwise treat as plain text.
const looksLikeHtml = (v) => typeof v === 'string' && /<\/?(?:p|br|strong|em|ul|ol|li|a|span|div)\b/i.test(v)

function MultiLineField({ label, value }) {
    const isHtml = looksLikeHtml(value)
    return (
        <div>
            <h2 className="text-sm font-semibold mb-2">{label}</h2>
            {isHtml ? (
                <div
                    className="text-sm text-gray-600 prose prose-sm max-w-none [&_p]:mb-1 [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_em]:italic [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-teal-600 [&_a]:underline"
                    dangerouslySetInnerHTML={{ __html: value }}
                />
            ) : (
                <div className="text-sm text-gray-600 whitespace-pre-line">{value || 'N/A'}</div>
            )}
        </div>
    )
}

const fmtMoney = (v) => {
    if (v === null || v === undefined || v === '') return null
    const n = Number(v)
    if (Number.isNaN(n)) return v
    return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

const fmtDateOrNull = (v) => {
    if (!v) return null
    const d = new Date(v.length === 10 ? v + 'T00:00:00' : v)
    if (isNaN(d.getTime())) return v
    // Match the rest of the case detail page: "May 04, 2026"
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' })
}

export default function LegalCase() {
    const { caseData, plaintiffPartyLabel, defendantPartyLabel, is_car_case, is_car_case_not_scheduled } = useContext(CaseDetailContext)
    if (!caseData) return null

    const meta = caseData.casemetadata || caseData.metadata || {}
    const isOdr = caseData.mediatorSettings?.is_odr_mediator
    const isCar = is_car_case || is_car_case_not_scheduled

    // CAR-case data may live in caseData.car_case_data.intake_form.form_data
    const carIntake = caseData.car_case_data?.intake_form?.form_data || {}

    if (isCar) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="bg-gray-50 p-6 border-b border-gray-200 rounded-t-md">
                    <div className="grid grid-cols-4 gap-4">
                        <div className="col-span-2">
                            <Field
                                label="Dispute Type"
                                labelClass="text-xs font-medium text-[#6B7280]"
                                value={carIntake.caseType || meta.case_type}
                            />
                        </div>
                        <Field
                            label="Dispute Value"
                            labelClass="text-xs font-medium text-[#6B7280]"
                            value={fmtMoney(carIntake.disputeValue || meta.dispute_value) || (carIntake.disputeValue || meta.dispute_value)}
                        />
                        <Field
                            label="Filed in court"
                            labelClass="text-xs font-medium text-[#6B7280]"
                            value={(carIntake.courtProceedings || meta.filed_in_court) ? 'Yes' : 'No'}
                        />
                    </div>
                </div>
            </div>
        )
    }

    // Standard case: 5-col top row when ODR (adds 2 ODR-only fields), else 4 cols
    const lastDemand = fmtMoney(meta.last_demand || meta.plaintiff_demand) || (meta.last_demand !== undefined ? '$0' : null)
    const lastOffer = fmtMoney(meta.last_offer || meta.defendant_demand) || (meta.last_offer !== undefined ? '$0' : null)

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="bg-gray-50 p-6 border-b border-gray-200 rounded-t-md">
                <div className={`grid gap-4 ${isOdr ? 'grid-cols-5' : 'grid-cols-4'}`}>
                    <Field label="Referring Judge" value={meta.referring_judge} />
                    <Field label="Case Type" value={meta.case_type} />
                    <Field label={`${plaintiffPartyLabel}'s Last Demand`} value={lastDemand} />
                    <Field label={`${defendantPartyLabel}'s Last Offer`} value={lastOffer} />
                    {isOdr && (
                        <Field
                            label="Has Litigation Been Filed"
                            value={meta.has_litigation_filed === true ? 'Yes' : meta.has_litigation_filed === false ? 'No' : null}
                        />
                    )}
                </div>
                {isOdr && meta.date_case_filed && (
                    <div className="mt-4">
                        <Field label="Date Case Filed" value={fmtDateOrNull(meta.date_case_filed)} />
                    </div>
                )}
            </div>
            <div className="p-6 space-y-6">
                <MultiLineField label={`${plaintiffPartyLabel}'s Causes of Action`} value={meta.causes_of_action || meta.cause_of_action} />
                <MultiLineField label="Remaining Discovery" value={meta.remaining_discovery} />
                <MultiLineField label="Remaining Law and Motion" value={meta.remaining_law_motion || meta.law_and_motion} />
            </div>
        </div>
    )
}
